<script setup lang="ts">
import { computed, nextTick, watch } from 'vue'
import { HelpTextTooltip, TextField, ChoiceField } from '@haskoning/vue-component-library'
import { useCalculationForm } from '@/components/calculation-form/composable/useCalculationForm'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'

const props = defineProps<CalculationFormMixinProps>()
const {
  calculationStore,
  currentItem,
  isValid,
  formRef,
  disabled,
  isReadOnly,
  hasHelpText,
  onFieldUpdate
} = useCalculationForm(props)

const projectInformationFields = ['name', 'type', 'phase', 'lifetime']
const editableChoiceFields = new Set(['type', 'phase'])

const fieldConfigMap = computed<Record<string, any>>(() => {
  const nextMap: Record<string, any> = {}
  projectInformationFields.forEach((fieldName) => {
    const fieldConfig = calculationStore.fields?.[fieldName]
    if (!fieldConfig) return
    if (!isReadOnly.value && editableChoiceFields.has(fieldName)) {
      nextMap[fieldName] = { ...fieldConfig, read_only: false }
      return
    }
    nextMap[fieldName] = fieldConfig
  })
  return nextMap
})

watch(
  () => currentItem.value,
  async (item) => {
    if (item && formRef.value) {
      await nextTick()
      await formRef.value.validate()
    }
  },
  { immediate: true }
)
</script>

<template>
  <v-container fluid class="pa-6">
    <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
      <v-card elevation="0" flat>
        <v-card-title class="header-font d-flex justify-space-between align-center">
          <h1 class="text-h4 mr-4">Calculation Data</h1>
        </v-card-title>
        <v-card-subtitle class="description-font mt-4">
          <p class="text-body-2 text-medium-emphasis mb-0">
            Parameters specific to the calculation
          </p>
        </v-card-subtitle>
        <v-card-text>
          <span class="sub-header-font"> Project information </span>
          <v-row align="center" no-gutters v-for="field in projectInformationFields" :key="field">
            <v-col cols="12" md="8" lg="4" class="d-flex align-center pb-4">
              <HelpTextTooltip
                v-if="hasHelpText(field)"
                :text="calculationStore.fields?.[field]?.help_text || ''"
              />
              <span :class="hasHelpText(field) ? 'px-2' : 'px-0'">
                {{ calculationStore.fields?.[field]?.label }}
              </span>
            </v-col>

            <v-col v-if="fieldConfigMap[field] && currentItem" cols="12" md="8" lg="4" class="pt-0">
              <TextField
                v-if="
                  fieldConfigMap[field].type === 'string' ||
                  fieldConfigMap[field].type === 'integer' ||
                  fieldConfigMap[field].type === 'float'
                "
                :field="fieldConfigMap[field]"
                :model-value="currentItem"
                :no-help-text="true"
                :readonly="isReadOnly"
                :disabled="isReadOnly"
                @update:model-value="onFieldUpdate"
              />
              <ChoiceField
                v-else-if="fieldConfigMap[field].type === 'choice'"
                :field="fieldConfigMap[field]"
                :model-value="currentItem"
                :no-help-text="true"
                :readonly="isReadOnly"
                :disabled="isReadOnly"
                @update:model-value="onFieldUpdate"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
  </v-container>
</template>

<style scoped></style>
